<script lang="ts">
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// Services
	import { format } from 'svelte-i18n';
	// Design System
	import { Text, Token, Input, IconButton } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { getDevicePropTranslation } from '../helpers/translation-helpers';
	// Validation helpers
	import { validatePresenceConditionData } from '../helpers/presence-helpers';
	// Presence-specific helpers
	import {
		type PresenceZone,
		getEffectivePresenceDeviceProps,
		validatePresenceCondition,
		buildPresenceCondition,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
	import { initializePresenceDeviceFromScene } from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-condition-extractor';
	import {
		createZonePropertyOptions,
		createLuminosityOptions,
		createIlluminanceOperatorOptions,
		handleZoneSelection,
		getInputStepValue,
		getDefaultInputValue,
		getMinInputValue,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-zone-manager';

	// Props
	export let sceneData: any = {};
	export let presenceZones: PresenceZone[] = [];
	export let disabled = false;

	// Event dispatcher
	const dispatch = createEventDispatcher<{
		conditionChange: { condition: any };
		validationChange: { isValid: boolean };
		devicePropChange: { deviceProp: any };
	}>();

	// Local state
	let selectedPresenceMode = ''; // 'zone_detection', 'luminosity', 'illuminance_threshold'
	let selectedZone = '';
	let selectedZoneProperty = '';
	let isUserInteracting = false; // Track when user is making changes
	let hasInitialized = false; // Track if we've initialized from scene data

	// Reactive statements
	$: effectiveDevicePropsOptions = getEffectivePresenceDeviceProps($format, presenceZones);

	// Get the current zone name for display
	$: currentZoneName = (() => {
		if (!selectedZone || !presenceZones.length) return '';
		const zone = presenceZones.find((z) => z.value === selectedZone);
		return zone ? zone.title : selectedZone.replace('zone_', 'Zone ');
	})();

	// Debug logging for presence device detection
	$: if (import.meta.env.DEV) {
		console.log('DEBUG: PresenceDeviceCondition state', {
			selectedPresenceMode,
			selectedZone,
			selectedZoneProperty,
			presenceZones,
			presenceZonesLength: presenceZones.length,
			sceneMode: sceneData.when_info?.mode,
			conditionValue: sceneData._device_condition?.value,
		});
	}

	// Initialize presence device form state when editing (only once, unless user hasn't interacted)
	$: {
		if (
			sceneData.when_info?.mode === 'edit' &&
			!hasInitialized &&
			!isUserInteracting &&
			(!selectedPresenceMode || !selectedZoneProperty)
		) {
			initializeFromScene();
		}
	}

	// Re-validate and update condition whenever any relevant state changes
	$: {
		if (selectedPresenceMode && selectedZoneProperty) {
			handlePresenceCondition();
		}
	}

	// Validate condition whenever state changes
	$: {
		const isValid = validatePresenceCondition(
			selectedPresenceMode,
			selectedZone,
			selectedZoneProperty,
		);
		dispatch('validationChange', { isValid });
	}

	const initializeFromScene = () => {
		const initialization = initializePresenceDeviceFromScene(sceneData);

		if (initialization) {
			selectedPresenceMode = initialization.selectedPresenceMode;
			selectedZone = initialization.selectedZone;
			selectedZoneProperty = initialization.selectedZoneProperty;

			// For band-based luminosity the value is the band string itself
			if (sceneData._device_condition) {
				sceneData._device_condition.value = initialization.conditionValue;
			}

			// Mark that we've initialized from scene data
			hasInitialized = true;
		}
	};

	// Handle input changes for numeric inputs (object count only)
	const handleInputChange = (e: Event) => {
		isUserInteracting = true;

		const target = e.target as HTMLInputElement;
		const value = target?.value || '';

		if (sceneData._device_condition) {
			sceneData._device_condition.value = value;
		}

		handlePresenceCondition();
		sceneData = sceneData;
	};

	// Handle presence device condition building
	const handlePresenceCondition = () => {
		if (!selectedPresenceMode || !selectedZoneProperty) {
			return;
		}

		const conditionValue =
			sceneData._device_condition?.value ||
			getDefaultInputValue(selectedPresenceMode, selectedZoneProperty);
		const condition = buildPresenceCondition(
			selectedPresenceMode,
			selectedZone,
			selectedZoneProperty,
			conditionValue,
		);

		if (condition) {
			// Validate the condition before proceeding
			const isValid = validatePresenceConditionData(
				{
					conditionName: condition.conditionName,
					deviceZoneId: condition.zoneAddInfoInd,
					value: condition.conditionValue,
					operator: condition.conditionOperator,
				},
				'PresenceDeviceCondition.handlePresenceCondition',
			);

			if (!isValid) {
				console.error('Invalid presence condition detected, not proceeding');
				return;
			}

			// Dispatch the condition change
			dispatch('conditionChange', {
				condition: {
					id: condition.conditionName,
					operator: condition.conditionOperator,
					value: condition.conditionValue,
					sensor_name: undefined,
					add_info_ind: condition.zoneAddInfoInd,
					input_index: undefined,
				},
			});
		}
	};

	const onSelectDeviceProp = (deviceProp: any) => {
		if (import.meta.env.DEV) {
			console.log('DEBUG: onSelectDeviceProp called', {
				deviceProp,
				previousMode: selectedPresenceMode,
				newMode: deviceProp.value,
			});
		}

		// Mark that user is actively making changes
		isUserInteracting = true;

		selectedPresenceMode = deviceProp.value;

		// Reset dependent selections
		selectedZone = '';
		selectedZoneProperty = '';

		// Dispatch device prop change
		dispatch('devicePropChange', { deviceProp });

		// Reset condition data
		if (sceneData._device_condition) {
			sceneData._device_condition.operator = '';
			sceneData._device_condition.value = getDefaultInputValue(selectedPresenceMode, '');
			sceneData._device_condition.cond_name = '';
			sceneData._device_condition.add_info_ind = undefined;
			sceneData._device_condition.input_index = undefined;
		}

		// Force reactivity update
		sceneData = sceneData;
	};

	const onSelectZone = (zoneValue: string) => {
		if (import.meta.env.DEV) {
			console.log('DEBUG: onSelectZone called', {
				zoneValue,
				previousZone: selectedZone,
				newZone: zoneValue,
			});
		}

		// Mark that user is actively making changes
		isUserInteracting = true;

		selectedZone = zoneValue;
		selectedZoneProperty = '';
		handleZoneSelection(zoneValue, sceneData);

		// Force reactivity update
		sceneData = sceneData;
	};

	const onSelectZoneProperty = (property: string) => {
		if (import.meta.env.DEV) {
			console.log('DEBUG: onSelectZoneProperty called', {
				property,
				previousProperty: selectedZoneProperty,
				newProperty: property,
			});
		}

		// Mark that user is actively making changes
		isUserInteracting = true;

		selectedZoneProperty = property;

		if (sceneData._device_condition) {
			// For luminosity bands the property value (dark/twilight/bright) IS the condition value.
			// For illuminance threshold, keep the current lux when switching lt/gt.
			// For zone-detection properties reset to the numeric default.
			if (selectedPresenceMode === 'luminosity') {
				sceneData._device_condition.value = property;
			} else if (selectedPresenceMode === 'illuminance_threshold') {
				const prev = sceneData._device_condition?.value;
				sceneData._device_condition.value = String(
					prev ?? getDefaultInputValue(selectedPresenceMode, property),
				);
			} else {
				sceneData._device_condition.value = getDefaultInputValue(selectedPresenceMode, property);
			}
		}

		handlePresenceCondition();

		// Force reactivity update
		sceneData = sceneData;
	};

	const adjustInputValue = (delta: number) => {
		// Mark that user is actively making changes
		isUserInteracting = true;

		const currentVal =
			Number(sceneData._device_condition?.value) ||
			parseInt(getDefaultInputValue(selectedPresenceMode, selectedZoneProperty));
		const step = getInputStepValue(selectedPresenceMode, selectedZoneProperty);
		const newVal = Math.max(
			getMinInputValue(selectedPresenceMode, selectedZoneProperty),
			currentVal + delta * step,
		);

		if (sceneData._device_condition) {
			sceneData._device_condition.value = newVal.toString();
		}

		handlePresenceCondition();

		// Force reactivity update
		sceneData = sceneData;
	};
</script>

<!-- Device Property Selection -->
<div class="d-flex flex-wrap gap-s">
	{#each effectiveDevicePropsOptions as deviceProp}
		<Token
			id="token-presence-prop-{deviceProp.value}"
			title={capFirstChar(deviceProp.title)}
			class="text-capitalize"
			on:click={() => onSelectDeviceProp(deviceProp)}
			selected={selectedPresenceMode === deviceProp.value}
			disabled={deviceProp.disabled || disabled}
		/>
	{/each}
</div>

{#if selectedPresenceMode}
	<!-- Zone Selection (if zone detection is selected) -->
	<div class="mt-m">
		{#if selectedPresenceMode === 'zone_detection' && presenceZones.length > 0}
			<Text class="mb-s" styleType="h2">
				{$format('select_zone', { default: 'Select Zone' })}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each presenceZones as zone}
					<Token
						id="token-zone-{zone.id}"
						title={capFirstChar(zone.title)}
						size="m"
						selected={selectedZone === zone.value}
						on:click={() => onSelectZone(zone.value)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}

		<!-- Zone Property Selection (after zone is selected) -->
		{#if selectedPresenceMode === 'zone_detection' && selectedZone}
			{@const zonePropertyOptions = createZonePropertyOptions($format)}

			<Text class="mb-s" styleType="h2">
				{$format('select_zone_property_for', {
					values: { zoneName: currentZoneName },
					default: `Select Property for ${currentZoneName}`,
				})}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each zonePropertyOptions as property}
					<Token
						id="token-zone-prop-{property.value}"
						title={property.title}
						size="m"
						selected={selectedZoneProperty === property.value}
						on:click={() => onSelectZoneProperty(property.value)}
						{disabled}
					/>
				{/each}
			</div>

			<!-- Object Count Input (if object_count is selected) -->
			{#if selectedZoneProperty === 'object_count'}
				<Text class="mb-s" styleType="h2">
					{$format('set_object_count_threshold_for', {
						values: { zoneName: currentZoneName },
						default: `Set Object Count Threshold for ${currentZoneName}`,
					})}
				</Text>
				<div class="d-flex mt-s mb-l">
					<IconButton
						aria-label={$format('button_decrease_value', {
							default: 'Decrease value',
						})}
						icon="fas fa-minus"
						on:click={() => adjustInputValue(-1)}
						disabled={Number(sceneData._device_condition?.value) <=
							getMinInputValue(selectedPresenceMode, selectedZoneProperty)}
					/>
					<Input
						aria-label={$format('input_set_object_count', {
							default: 'Set object count',
						})}
						{...getNumberInputConfig(true)}
						class="width-md-s width-sm-full ml-s mr-s"
						size="m"
						value={sceneData._device_condition?.value ||
							getDefaultInputValue(selectedPresenceMode, selectedZoneProperty)}
						on:input={handleInputChange}
						id="input-object-count"
						min={getMinInputValue(selectedPresenceMode, selectedZoneProperty).toString()}
						type="number"
					/>
					<IconButton
						aria-label={$format('button_increase_value', {
							default: 'Increase value',
						})}
						icon="fas fa-plus"
						on:click={() => adjustInputValue(1)}
					/>
				</div>
			{/if}
		{/if}

		<!-- Luminosity Options — three discrete bands: Dark / Twilight / Bright (`lux`) -->
		{#if selectedPresenceMode === 'luminosity'}
			{@const luminosityOptions = createLuminosityOptions($format)}

			<Text class="mb-s" styleType="h2">
				{getDevicePropTranslation('luxury', '', undefined, '')?.title}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each luminosityOptions as lux}
					{@const t = getDevicePropTranslation('luxury', '', undefined, lux.value)}
					<Token
						id="token-lux-{lux.value}"
						title={t?.condition ?? lux.title}
						size="m"
						icon={t?.icons[lux.value]}
						selected={selectedZoneProperty === lux.value}
						on:click={() => onSelectZoneProperty(lux.value)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}

		<!-- Numeric illuminance threshold (`lux_i`), same as other sensors’ Illumination condition -->
		{#if selectedPresenceMode === 'illuminance_threshold'}
			{@const luxOpOptions = createIlluminanceOperatorOptions($format)}
			<Text class="mb-s" styleType="h2">
				{$format('collapse_title_illumination', { default: 'Illumination' })}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each luxOpOptions as op}
					<Token
						id="token-lux-op-{op.value}"
						title={op.title}
						size="m"
						selected={selectedZoneProperty === op.value}
						on:click={() => onSelectZoneProperty(op.value)}
						{disabled}
					/>
				{/each}
			</div>
			{#if selectedZoneProperty === 'lt' || selectedZoneProperty === 'gt'}
				<Text class="mb-s" styleType="h2">
					{$format('label_illuminance_lux', { default: 'Illuminance (lux)' })}
				</Text>
				<div class="d-flex mt-s mb-l">
					<IconButton
						aria-label={$format('button_decrease_value', {
							default: 'Decrease value',
						})}
						icon="fas fa-minus"
						on:click={() => adjustInputValue(-1)}
						disabled={Number(sceneData._device_condition?.value) <=
							getMinInputValue(selectedPresenceMode, selectedZoneProperty)}
					/>
					<Input
						aria-label={$format('input_set_illuminance_lux', {
							default: 'Set illuminance in lux',
						})}
						{...getNumberInputConfig(true)}
						class="width-md-s width-sm-full ml-s mr-s"
						size="m"
						value={sceneData._device_condition?.value ||
							getDefaultInputValue(selectedPresenceMode, selectedZoneProperty)}
						on:input={handleInputChange}
						id="input-presence-lux-i"
						min={getMinInputValue(selectedPresenceMode, selectedZoneProperty).toString()}
						type="number"
					/>
					<IconButton
						aria-label={$format('button_increase_value', {
							default: 'Increase value',
						})}
						icon="fas fa-plus"
						on:click={() => adjustInputValue(1)}
					/>
				</div>
			{/if}
		{/if}
	</div>
{/if}
